import { AddOutlined, Cancel, Delete, EditRounded } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import { Button } from "antd";
import { DeleteModal } from "components/Dialog";
import Loading from "components/Loading";
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import React, { useEffect, useMemo, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useDispatch } from "react-redux";
import { useGetAllSlidesData } from "store/slides/selectors";
import {
  handleChangeImageUrl,
  handleCreateSlides,
  handleDeleteSlide,
  handleDeleteSlideImage,
  handleSwapOrder,
  handleUpdateNotes,
  handleUpdateRectangle,
} from "store/slides/thunk";
import styled from "styled-components";
import { generateShortUUID } from "utils/methods/math";
import NoteEditor from "../NoteEditor";
import SlideHighLighter from "../slideHighlighter";
import SentryWrap from 'components/Sentry';
const ItemTypes = {
  SLIDE: "slide",
};

const isTouchDevice = () => {
  try {
    return (
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );
  } catch (e) {
    Sentry.captureException(`608 Error in isTouchDevice: ${e}`);
    return false;
  }
};

// Error Alert Component
const ErrorAlert = ({ message, onClose }) => (
  <ErrorContainer>
    <ErrorMessage>{message}</ErrorMessage>
    <ErrorCloseButton onClick={onClose}>×</ErrorCloseButton>
  </ErrorContainer>
);

// Main Component
const HorizontalSlideViewer = ({ bookId, pageId, book, setOpenModal , is_resourse }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [openTextEditor, setOpenTextEditor] = useState(false);
  const [imageConfiguration, setImageConfiguration] = useState({});
  const [slides, setSlides] = useState([]);
  const [error, setError] = useState(null);
  const [slideLoading, setSlideLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const dispatch = useDispatch();
  const allSlideData = useGetAllSlidesData();
  const showAlert = useAlert();
  const validateSlidesBeforeSave = () => {
    const emptySlides = slides.filter(slide => !slide.src);

    if (emptySlides.length > 0) {
      showAlert("Some slides are missing images! Please upload an image or delete empty slides.", "error");
      return false; // Prevent saving
    }

    return true; // Allow saving
  };

  const handleSave = () => {
    if (!validateSlidesBeforeSave()) {
      return; // Stop save process if validation fails
    }

    setOpenModal(false); // Close modal if validation passes
  };
  const [loaderType, setLoaderType] = useState({
    type: "",
    id: "",
    value: false,
  });
  const [openDeleteEbookModel, setOpenDeleteEbookModel] = useState(false);
  const getSlideData = useMemo(() => {
    if (allSlideData?.length > 0) {
      if(is_resourse){
       return  allSlideData.find(slide => slide.book_id == bookId)
      }
      return (
        allSlideData.find(
          (x) =>
            Number(x?.book_id) === Number(bookId) &&
            Number(x?.page_id) === Number(pageId)
        ) || {}
      );
    } else {
      return {};
    }
  }, [bookId, pageId, allSlideData,is_resourse]);

  const touchDevice = useMemo(() => {
    return isTouchDevice();
  }, []);

  const currentSlideData = useMemo(() => {
    if (!slides) return {};
    if (currentSlide < 0 || currentSlide >= slides.length) return {};
    return slides[currentSlide] || {};
  }, [currentSlide, slides]);

  useEffect(() => {
    let slideData = {};
    if (is_resourse) {
      slideData = allSlideData.find(slide => slide.book_id == bookId)
    } else {
      slideData =
        allSlideData.find(
          (x) =>
            Number(x?.book_id) === Number(bookId) &&
            Number(x?.page_id) === Number(pageId)
        ) || {};
    }
    if (slideData?.pages) {
      setSlides(slideData?.pages?.filter((x) => x !== null) || []);
    }
  }, [bookId, pageId, allSlideData]);

  const moveSlide = async (dragIndex, dropIndex) => {
    try {
      const newSlides = [...slides];
      const [draggedSlide] = newSlides.splice(dragIndex, 1);
      newSlides.splice(dropIndex, 0, draggedSlide);

      if (currentSlide === dragIndex) {
        setCurrentSlide(dropIndex);
      } else if (currentSlide > dragIndex && currentSlide <= dropIndex) {
        setCurrentSlide(currentSlide - 1);
      } else if (currentSlide < dragIndex && currentSlide >= dropIndex) {
        setCurrentSlide(currentSlide + 1);
      }

      setSlides(newSlides);
      await dispatch(
        handleSwapOrder({
          id: getSlideData?.id,
          pages: newSlides,
        })
      );
    } catch (err) {
      Sentry.captureException(`609 Error in moveSlide: ${err}`);
      console.error("Error moving slide:", err);
      setError("Failed to reorder slides");
    }
  };

  // const addSlide = async () => {
  //   setSlideLoading(true);
  //   try {
  //     const newSlide = {
  //       id: generateShortUUID(),
  //       src: "",
  //       notes: "",
  //       data: [],
  //     };
  //     const newSlides = [...slides, newSlide];
  //     const res = await dispatch(
  //       handleCreateSlides({
  //         book_id: bookId,
  //         page_id: pageId,
  //         pages: newSlides,
  //       })
  //     );
  //     if (res?.id) {
  //       setSlides(newSlides);
  //     }
  //   } catch (err) {
  //     console.error("Error adding slide:", err);
  //     setError("Failed to add new slide");
  //   } finally {
  //     setSlideLoading(false);
  //   }
  // };

  const addSlide = async () => {
    if (slides.some(slide => !slide.src)) {
      showAlert("You have an empty slide! Please upload an image or delete it before adding more.", "warning");
      return;
    }

    setSlideLoading(true);
    try {
      const newSlide = { id: generateShortUUID(), src: "", notes: "", data: [] };
      const newSlides = [...slides, newSlide];

      const res = await dispatch(
        handleCreateSlides({ book_id: bookId, page_id: pageId, pages: newSlides })
      );

      if (res?.id) {
        setSlides(newSlides);
      }
    } catch (err) {
      Sentry.captureException(`610 Error in addSlide: ${err}`);
      showAlert("Failed to add new slide", "error");
    } finally {
      setSlideLoading(false);
    }
  };


  const deleteSlide = async (id, deletedIndex) => {
    setDeleteLoading(id);

    try {
      const data = {
        id: getSlideData?.id,
        slide_id: id,
      };
      const deleteData = await dispatch(handleDeleteSlideImage(data));
      if (deleteData?.id) {
        showAlert("Slide deleted successfully!", "success");
        setSlides(deleteData?.pages);
        if (deletedIndex === currentSlide) {
          if (deleteData?.pages.length > deletedIndex) {
            setCurrentSlide(deletedIndex);
          } else if (deletedIndex > 0) {
            setCurrentSlide(deletedIndex - 1);
          }
        }
      } else {
        showAlert("Failed to delete slide, Please try again!", "error");
      }
    } catch (err) {
      Sentry.captureException(`611 Error in deleteSlide: ${err}`);
      console.error("Error deleting slide:", err);
      setError("Failed to delete slide");
    } finally {
      setDeleteLoading("");
    }
  };

  const handleDeleteEbookSlides = async (id) => {
    setLoaderType((prev) => ({
      ...prev,
      value: true,
      type: "deleteAll",
      id: id,
    }));
    try {
      const deleteData = await dispatch(handleDeleteSlide(getSlideData?.id));
      if (deleteData?.id) {
        setOpenModal(false);
        setOpenDeleteEbookModel(false);
        showAlert("Ebook slide deleted successfully!", "success");
      }
    } catch (err) {
      Sentry.captureException(`612 Error in handleDeleteEbookSlides: ${err}`);
      console.error("Error deleting slide:", err);
      showAlert("Failed to delete slide", "error");
    } finally {
      setLoaderType({});
    }
  };

  const handleActiveSlide = (index) => {
    if (loaderType?.value === true) {
      showAlert("Uploading in progress", "error");
      return;
    }
    setCurrentSlide(index);
  };

  const handleTextOpenEditor = () => {
    setOpenTextEditor(true);
  };

  const handleEditTextEditor = (value) => {
    setSlides((prev) =>
      prev.map((element, index) =>
        index === currentSlide ? { ...element, notes: value || "" } : element
      )
    );
  };

  const updateSlideProperty = (key, value) => {
    setSlides((prev) =>
      prev.map((element, index) =>
        index === currentSlide ? { ...element, [key]: value || "" } : element
      )
    );
  };

  const handleSaveText = async () => {
    try {
      setNotesLoading(true);
      const saveNotes = await dispatch(
        handleUpdateNotes({
          id: getSlideData?.id,
          slide_id: currentSlideData?.id,
          notes: currentSlideData.notes,
        })
      );
      if (saveNotes?.id) {
        // Update local slides state with the saved data
        if (saveNotes?.pages) {
          setSlides(saveNotes.pages.filter((x) => x !== null) || []);
        }
        showAlert("Notes added successfully!", "success");
      } else {
        showAlert("Fail to add notes, please try again", "error");
      }
      setNotesLoading(false);
      setOpenTextEditor(false);
    } catch (err) {
      Sentry.captureException(`613 Error in handleSaveText: ${err}`);
      setError("Failed to save text");
      setNotesLoading(false);
    }
  };

  const handleSaveRectangle = async (rectangles, natural_dimensions) => {
    try {
      updateSlideProperty("data", rectangles);
      await dispatch(
        handleUpdateRectangle({
          id: getSlideData?.id,
          slide_id: currentSlideData.id,
          data: rectangles,
          natural_dimensions: natural_dimensions,
        })
      );
    } catch (error) {
      Sentry.captureException(`614 Error in handleSaveRectangle: ${error}`);
      setError("Failed to save highlight");
    }
  };

  return (
    <SentryWrap>
      <UpperContainer className="flex items-center justify-between w-full">
        <Cancel
          onClick={() => {
            if (loaderType?.value === true) {
              showAlert("Uploading in progress", "error");
              return;
            }
            handleSave();
            // if (!validateSlidesBeforeSave()) {
            //   return;
            // }
            // else {
            //   setOpenModal(false);
            // }
          }}
          className="cancel-icon-ppt"
        />
        <SaveBtnContainer className='slidesSaveOptn'>
        {getSlideData?.id && (
          <Button onClick={() => setOpenDeleteEbookModel(true)}>
            Delete All slide
          </Button>
        )}
        {getSlideData?.id && (
            <Button onClick={handleSave}>
            Save
          </Button>
          )}
        </SaveBtnContainer>
      </UpperContainer>
      {error && <ErrorAlert message={error} onClose={() => setError(null)} />}
      <NoteEditor
        data={currentSlideData}
        open={openTextEditor}
        close={setOpenTextEditor}
        index={currentSlide}
        onChange={handleEditTextEditor}
        saveTextHandler={handleSaveText}
        notesLoading={notesLoading}
      />
      <DeleteModal
        deleteToggle={openDeleteEbookModel}
        setDeleteToggle={setOpenDeleteEbookModel}
        message="Are you sure you want to delete slide ebook?"
        onDelete={handleDeleteEbookSlides}
        loading={loaderType?.type === "deleteAll" && loaderType?.value === true}
      />
      <Container>
        <MainContent>
          <SlideContainer>
            <SlideWrapper>
              <SlideContent>
                {slides?.length === 0 ? (
                  <>
                    <AddImagePlaceHolder className="">
                      <AddOutlined size={20} />
                      Please add slide
                    </AddImagePlaceHolder>
                  </>
                ) : (
                  <SlideHighLighter
                    getBookSlides={getSlideData}
                    imageUrl={currentSlideData?.src || ""}
                    pageId={pageId}
                    bookId={bookId}
                    isNonNative={
                      book?.book_type === "non_native_spanish" || false
                    }
                    setImageConfiguration={setImageConfiguration}
                    setpagePoster={(url) => updateSlideProperty("src", url)}
                    handleSaveRectangle={handleSaveRectangle}
                    activeSlide={currentSlide}
                    rectangles={currentSlideData?.data}
                    setRectangles={(data) => updateSlideProperty("data", data)}
                    setLoaderType={setLoaderType}
                    onLoading={(val) => {
                      if(val === false){
                        setLoaderType({});
                        return;
                      }
                      setLoaderType((prev) => ({
                        ...prev,
                        value: val,
                        type: "uploader",
                      }));
                    }}
                    onChange={async ({ url }) => {
                      updateSlideProperty("src", url);
                      await dispatch(
                        handleChangeImageUrl({
                          id: getSlideData?.id,
                          slide_id: currentSlideData?.id,
                          src: url,
                        })
                      );
                    }}
                  />
                )}
              </SlideContent>
            </SlideWrapper>
          </SlideContainer>
        </MainContent>

        <SliderMainContainer>
          {slides?.length !== 0 && (
            <div>
              <ActionButton className='editNoteBtn' color="black" onClick={handleTextOpenEditor}>
                <EditRounded size={16} color="black" /> &nbsp; Edit Note
              </ActionButton>
            </div>
          )}

          <SliderContainer>
            <ThumbnailsWrapper className="slideThumbnailCstm">
                {slides.map((slide, index) => (
                  <SlideThumbnail
                    key={slide?.id}
                    slide={slide}
                    index={index}
                    isActive={currentSlide === index}
                    onMove={moveSlide}
                    onDelete={(id) => deleteSlide(id, index)}
                    onSelect={handleActiveSlide}
                    deleteLoading={deleteLoading}
                  />
                ))}
                <AddSlideButton onClick={addSlide} disabled={slideLoading}>
                  {slideLoading ? (
                    <Loading size={18} white={true} />
                  ) : (
                    <AddOutlined size={20} />
                  )}
                </AddSlideButton>
            </ThumbnailsWrapper>
          </SliderContainer>
        </SliderMainContainer>
      </Container>
    </SentryWrap>
  );
};

const SlideThumbnail = ({
  slide,
  index,
  isActive,
  onMove,
  onDelete,
  onSelect,
  deleteLoading,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.SLIDE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.SLIDE,
    drop: (item) => onMove(item.index, index),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <ThumbnailContainer
      ref={(node) => drag(drop(node))}
      isActive={isActive}
      isDragging={isDragging}
      isDraggedOver={isOver}
      onClick={() => onSelect(index, slide)}
    >
      <ThumbnailContent>
        {slide?.src ? (
          <img
            style={{ width: "100%", height: "100%" }}
            className="prevent-select"
            src={slide?.src}
            alt="Slide thumbnail"
          />
        ) : (
          <div className="flex items-center">
            <AddOutlined /> Add Image
          </div>
        )}
      </ThumbnailContent>

      <ThumbnailOverlay>
        <ActionButtons className='slideThumbnailDel'>
          <ActionButton
            color="red"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(slide?.id);
            }}
          >
            {deleteLoading === slide?.id ? (
              <Loading size={18} />
            ) : (
              <Delete size={16} />
            )}
          </ActionButton>
        </ActionButtons>
      </ThumbnailOverlay>
    </ThumbnailContainer>
  );
};

export default HorizontalSlideViewer;

const UpperContainer = styled.div`
  padding-inline: 12px;
  margin-top: 10px;
`;

const AddImagePlaceHolder = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid black;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  background-color: #e5e5e5;
`;

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  height: 95%;
  padding: 12px;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 2rem;
`;

const SlideContainer = styled.div`
  max-width: 700px;
  margin: 20px auto 0px;
`;

const SlideWrapper = styled.div`
  aspect-ratio: 16/9;
  border-radius: 0.75rem;
  position: relative;
`;

const SlideContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const SliderMainContainer = styled.div`
  background-color: #fff;
  margin-top: 82px;
`;

const SliderContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-top: 1px solid #e5e7eb;
  overflow-x: auto;
  overflow-y: hidden;
  max-height: 163px;
`;

const ThumbnailsWrapper = styled.div`
  display: flex;
  gap: 1rem;
`;

const ThumbnailContainer = styled.div`
  position: relative;
  min-width: 200px;
  aspect-ratio: 16/9;
  background: white;
  border-radius: 0.5rem;
  cursor: grab;
  overflow: hidden;
  border: 5px solid ${(props) => (props.isActive ? "#03989E" : "transparent")};
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  transform: ${(props) => (props.isDraggedOver ? "scale(1.05)" : "scale(1)")};
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }
`;

const ThumbnailContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ThumbnailOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  transition: opacity 0.2s ease;
`;

const ActionButtons = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.375rem;
  background: white;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  color: ${(props) => (props.color ? props.color : "black")};

  &:hover {
    background-color: #f3f4f6;
  }
`;

const AddSlideButton = styled.button`
  min-width: 200px;
  aspect-ratio: 16/9;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #03989e;
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
`;

const ErrorContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #ff5252;
  color: white;
  padding: 12px 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1000;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;
const SaveBtnContainer = styled.div`
    display: flex;
    gap:10px;
`;

const ErrorMessage = styled.span`
  font-size: 14px;
`;

const ErrorCloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  &:hover {
    opacity: 0.8;
  }
`;
